import { ElementSubType } from '@portal/app/shared/types';
import {
  IDToken,
  OktaAuth,
  SHARED_TRANSACTION_STORAGE_NAME
} from '@okta/okta-auth-js';

export const titleBase = 'Portal |';

const getUriSegments = (href: string): string[] =>
  new URL(href).pathname.split('/');

export const getNextToLastUriSegment = (href: string): string => {
  const segments = getUriSegments(href);
  if (segments.length > 0) {
    const lastSegment = segments.pop();
    if (lastSegment != null) {
      // Handle potential trailing slash
      if (lastSegment !== '/') {
        const secondToLast = segments.pop();
        return secondToLast != null ? secondToLast : '';
      } else {
        segments.pop();
        const trueSecondToLast = segments.pop();
        return trueSecondToLast != null ? trueSecondToLast : '';
      }
    }
  }
  return '';
};

export const sortElementSubTypeByDisplayOrder = (
  a: ElementSubType,
  b: ElementSubType
): 0 | -1 | 1 => {
  if (a.displayOrder != null && b.displayOrder != null) {
    return a.displayOrder > b.displayOrder
      ? 1
      : a.displayOrder < b.displayOrder
        ? -1
        : 0;
  }
  return 0;
};

export const getSorted = <T>(items: T[], comparator: string): T[] =>
  items.sort((a: T | null, b: T | null) => {
    const typedA = a as unknown as Record<string, number> | null;
    const typedB = b as unknown as Record<string, number> | null;
    if (typedA != null && typedB != null) {
      const aSide = typedA[comparator];
      const bSide = typedB[comparator];
      if (aSide != null && bSide != null) {
        return aSide > bSide ? 1 : aSide === bSide ? 0 : -1;
      }
      return 0;
    }
    return 0;
  });

export const slugify = (text: string, separator?: string): string => {
  let transformedText = text.toString().toLowerCase().trim();

  const sets = [
    { to: 'a', from: '[ÀÁÂÃÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶΑΆ]' },
    { to: 'b', from: '[Β]' },
    { to: 'ae', from: '[Ä]' },
    { to: 'c', from: '[ÇĆĈČ]' },
    { to: 'd', from: '[ÐĎĐÞΔ]' },
    { to: 'e', from: '[ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆΕΈ]' },
    { to: 'f', from: '[Φ]' },
    { to: 'g', from: '[ĜĞĢǴΓ]' },
    { to: 'h', from: '[ĤḦ]' },
    { to: 'i', from: '[ÌÍÎÏĨĪĮİỈỊΗΉΙΊΪΐ]' },
    { to: 'j', from: '[Ĵ]' },
    { to: 'ij', from: '[Ĳ]' },
    { to: 'k', from: '[ĶΚ]' },
    { to: 'ks', from: '[Ξ]' },
    { to: 'l', from: '[ĹĻĽŁΛ]' },
    { to: 'm', from: '[ḾΜ]' },
    { to: 'n', from: '[ÑŃŅŇΝ]' },
    { to: 'o', from: '[ÒÓÔÕØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠΟΌΩΏ]' },
    { to: 'oe', from: '[ŒÖ]' },
    { to: 'p', from: '[ṕΠ]' },
    { to: 'ps', from: '[Ψ]' },
    { to: 'r', from: '[ŔŖŘΡ]' },
    { to: 's', from: '[ŚŜŞŠΣς]' },
    { to: 'ss', from: '[ß]' },
    { to: 't', from: '[ŢŤΤ]' },
    { to: 'th', from: '[Θ]' },
    { to: 'u', from: '[ÙÚÛŨŪŬŮŰŲỤỦỨỪỬỮỰƯΥΎΫΰ]' },
    { to: 'ue', from: '[Ü]' },
    { to: 'w', from: '[ẂŴẀẄ]' },
    { to: 'x', from: '[ẍΧ]' },
    { to: 'y', from: '[ÝŶŸỲỴỶỸ]' },
    { to: 'z', from: '[ŹŻŽΖ]' },
    // eslint-disable-next-line quotes,@typescript-eslint/quotes
    { to: '-', from: "[·/_,:;']" }
  ];

  sets.forEach((set) => {
    transformedText = transformedText.replace(
      new RegExp(set.from, 'gi'),
      set.to
    );
  });

  transformedText = transformedText
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/&/g, '-and-') // Replace & with 'and'
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text

  if (typeof separator !== 'undefined' && separator !== '-') {
    transformedText = transformedText.replace(/-/g, separator);
  }

  return transformedText;
};

export const getOktaIdTokenObj = async (
  oktaAuth?: OktaAuth
): Promise<IDToken | undefined> => {
  try {
    if (oktaAuth) {
      const token = await oktaAuth.tokenManager.get('idToken');
      return token as IDToken;
    }

    const oktaIdTokenObject: IDToken = JSON.parse(
      localStorage.getItem('okta-token-storage') || '{}'
    )?.idToken;

    return oktaIdTokenObject;
  } catch {
    return undefined;
  }
};

export const getIdToken = async (
  oktaAuth?: OktaAuth
): Promise<string | null> => {
  const oktaIdTokenObject = await getOktaIdTokenObj(oktaAuth);
  return oktaIdTokenObject?.idToken || null;
};

/**
 * Check if there's an active Okta transaction in progress.
 * This indicates the user is in the middle of the PKCE login flow.
 * Uses Okta's exported constant for the storage key name.
 */
export const isLoginInProgress = (): boolean => {
  return !!localStorage.getItem(SHARED_TRANSACTION_STORAGE_NAME);
};

const isLoginPagePath = (url: string): boolean => {
  const pathOnly = url.split('?')[0] ?? '';
  return (
    pathOnly === '/login' ||
    pathOnly.startsWith('/login/') ||
    pathOnly === '/force-login'
  );
};

/**
 * Strips any returnUrl query parameter from a URL to prevent circular nesting.
 */
const stripReturnUrlParam = (url: string): string => {
  if (!url.includes('?')) {
    return url;
  }

  const [path = '', ...rest] = url.split('?');
  const queryString = rest.join('?');

  const params = new URLSearchParams(queryString);
  params.delete('returnUrl');

  const remainingParams = params.toString();
  return remainingParams ? `${path}?${remainingParams}` : path;
};

/**
 * Returns the URL as a valid returnUrl, or null if it's a login page.
 * Handles array input (takes the last value) and strips nested returnUrl params.
 */
export const getReturnUrl = (
  url: string | string[] | null | undefined
): string | null => {
  const urlValue = Array.isArray(url) ? url[url.length - 1] : url;

  if (!urlValue || isLoginPagePath(urlValue)) {
    return null;
  }

  return stripReturnUrlParam(urlValue);
};
